import React, {useCallback, useContext, useEffect, useState, useRef} from 'react';
import {List} from 'immutable';
import classnames from 'classnames';
import {pluralize} from 'lib/stringUtils';
import {
  List as VirtualizedList,
  AutoSizer,
  CellMeasurer,
  CellMeasurerCache
} from 'react-virtualized';
import {debounce} from 'lodash';
import {easeInOutCirc} from 'js-easing-functions';
import {useTracking} from 'react-tracking';

import {
  Button,
  Icon,
  ListGroup,
  ListGroupItem,
  LoadingSpinner,
  Text,
  WithHoverAndFocus,
  WithTooltip
} from '@albert-io/atomic';

import MarkdownRendererV2 from 'generic/MarkdownRendererV2/MarkdownRendererV2.react';

import QuestionsListFooter from 'components/PracticeView/QuestionsList/QuestionsListFooter.react';

import getDifficultyValueFromInt from 'lib/getDifficultyValueFromInt';

import {Question} from 'client/Classrooms/Classroom/Teacher/Assignments/ViewStudentSubmission/ViewStudentSubmission.types';

import {ContentDiscoveryContext} from '../ContentDiscoveryProvider.react';

import AddQuestionsDropdown from './AddQuestionsDropdown.react';

import './content-discovery-question-list.scss';

interface Props {
  activeQuestion: Question;
  scrollToQuestionByIdFunc: any;
  setActiveQuestionState: (question: any, questionSet: any) => void;
  paginatedResultsManager: any;
  totalQuestions: null | number;
}

const ContentDiscoveryAddQuestionsList = ({
  activeQuestion,
  scrollToQuestionByIdFunc,
  setActiveQuestionState,
  paginatedResultsManager,
  totalQuestions
}: Props) => {
  const cellMeasurerCache = useRef<CellMeasurerCache>();
  const listRef = useRef<VirtualizedList>(null);
  const scrollPosition = useRef(0);
  const [isLoadingQuestionsList, setIsLoadingQuestionsList] = useState(false);

  const {
    autoSave,
    searchSort,
    setSearchSort,
    selectedQuestionSets,
    setSelectedQuestionSets,
    getSelectedStandardsAndSets,
    hasSelectedStandard,
    standardCollection,
    addQuestionSetsToDraft,
    isQuestionSetSelected,
    removeQuestionSet
  } = useContext(ContentDiscoveryContext);

  const {trackEvent} = useTracking({
    component: 'add questions list',
    type: 'button',
    event: 'click'
  });

  const questionSets = paginatedResultsManager?.getResults();

  // Exists in all question lists
  const smoothScrollToPosition = useCallback(
    (endPos, startPos = scrollPosition.current, startTime = Date.now()) => {
      const duration = 1000;
      const elapsed = Date.now() - startTime;
      const nextPos = easeInOutCirc(elapsed, startPos, endPos - startPos, duration);
      listRef.current?.scrollToPosition(nextPos);
      if (elapsed < duration) {
        global.requestAnimationFrame(() => {
          smoothScrollToPosition(endPos, startPos, startTime);
        });
      }
    },
    []
  );

  // Exists in all question lists
  useEffect(() => {
    cellMeasurerCache.current = new CellMeasurerCache({
      fixedWidth: true,
      minHeight: 30
    });
  }, []);

  // Exists in all question lists
  useEffect(() => {
    const scrollToQuestion = (questionSetId) => {
      const index = questionSets.findIndex((questionSet) => questionSet!.getId() === questionSetId);
      if (index === undefined) {
        return;
      }
      const offset = listRef!.current!.getOffsetForRow({
        alignment: 'start',
        index
      });
      smoothScrollToPosition(offset);
    };

    scrollToQuestionByIdFunc.current = scrollToQuestion;
  }, [scrollToQuestionByIdFunc, questionSets, smoothScrollToPosition]);

  // Handles fetching more questions
  // Exists in all question lists
  const handleRowsRendered = debounce(async ({stopIndex}) => {
    const isLastQuestionScrolledIntoView = stopIndex + 1 === questionSets.size;
    if (!paginatedResultsManager.isRequestInFlight) {
      if (isLastQuestionScrolledIntoView) {
        setIsLoadingQuestionsList(true);
        await paginatedResultsManager.fetchNextPage();
        setIsLoadingQuestionsList(false);
      }
    }
  }, 250);

  const updateScrollPosition = ({scrollTop}) => {
    scrollPosition.current = scrollTop;
  };

  const addQuestionSet = useCallback(
    (e, questionSet) => {
      trackEvent({content: `add ${questionSet.getName()}`});
      if (autoSave) {
        addQuestionSetsToDraft(List([questionSet]));
      } else {
        const newSelectedSets = List([...selectedQuestionSets.toArray(), questionSet]); // TODO: handle duplicates
        setSelectedQuestionSets(newSelectedSets);
      }
      e.stopPropagation();
    },
    [autoSave, addQuestionSetsToDraft, setSelectedQuestionSets, selectedQuestionSets, trackEvent]
  );

  const getMatchingStandards = useCallback(
    (questionSet) => {
      if (!hasSelectedStandard()) {
        return [];
      }
      const standards = questionSet?.getQuestions().first()?.getStandards() || [];
      const {selectedStandardIds, selectedStandardSetIds} =
        getSelectedStandardsAndSets(standardCollection);

      // standard selected individually
      const matchingStandards = standards.filter((standard) => {
        return selectedStandardIds.includes(standard.getId());
      });
      // standard set selected
      const matchingStandardSets = standards.filter((standard) => {
        const standardSet = standard.getStandardSet();
        return selectedStandardSetIds.includes(standardSet.getId());
      });

      const allSelected = [...matchingStandardSets.toArray(), ...matchingStandards.toArray()];

      return allSelected;
    },
    [getSelectedStandardsAndSets, hasSelectedStandard]
  );

  // @ts-ignore
  if (!activeQuestion || Object.keys(activeQuestion).length === 0) {
    return <div>We do not have an active question right now</div>;
  }

  const rowRenderer = ({index, key, parent, style}) => {
    const questionSet = questionSets.get(index);
    const questionSetDifficulty = getDifficultyValueFromInt(
      questionSet?.getQuestions().get(0).getDifficulty(),
      true
    );
    const matchingQuestionSetStandards = getMatchingStandards(questionSet);
    const questionSetType = questionSet
      ?.getQuestions()
      .first()
      .getQuestionType()
      .replace(/-/g, ' ');

    const tooltipText = matchingQuestionSetStandards
      .map((standard) => standard.getTitle())
      .join(', ');

    return (
      <CellMeasurer
        cache={cellMeasurerCache.current!}
        columnIndex={0}
        key={key}
        rowIndex={index}
        parent={parent}
      >
        <ListGroupItem
          id={questionSet?.getId()}
          paddingLevel={1}
          as='button'
          tabIndex={0}
          onClick={() => {
            setActiveQuestionState(questionSet?.getQuestions().first(), questionSet);
          }}
          style={style}
          className='question-v2 u-cursor_pointer u-display_flex u-justify-content_space-between'
        >
          <div className='u-display_flex u-justify-content_space-between u-text-align_left u-flex-grow_1 u-align-items_center '>
            <div
              className={classnames(
                'u-display_flex u-align-items_center u-flex_1 cd-question-list__question u-gap_1',
                {
                  'cd-question-list__question--selected':
                    questionSet?.getId() === activeQuestion.getQuestionSet?.().getId()
                }
              )}
            >
              <div className='u-display_flex u-flex-direction_column u-flex_1'>
                <Text
                  as={MarkdownRendererV2}
                  text={questionSet?.getQuestions()!.get(0)?.getTitle()}
                  size='m'
                  bold
                />
                <div className='cd-question-list__question-metadata'>
                  <Text
                    as='div'
                    className={`cd-question-list__question-metadata-text cd-questionlist__difficulty cd-questionlist__difficulty-${questionSetDifficulty}`}
                  >
                    {questionSetDifficulty}
                  </Text>
                  <Text as='div' className='cd-question-list__question-metadata-text'>
                    {questionSetType}
                  </Text>
                  {hasSelectedStandard() && (
                    <>
                      •
                      <Text
                        as='div'
                        className='cd-question-list__standards cd-question-list__question-metadata-text'
                      >
                        <WithTooltip content={tooltipText}>
                          {`${matchingQuestionSetStandards.length} ${pluralize(
                            'standard',
                            matchingQuestionSetStandards.length
                          )}`}
                        </WithTooltip>
                      </Text>
                    </>
                  )}
                </div>
              </div>
              {isQuestionSetSelected(questionSet) ? (
                <WithHoverAndFocus>
                  {({hasHover, onMouseEnter, onMouseLeave}) => (
                    <div
                      className='u-display_flex u-align-items_center'
                      onMouseEnter={onMouseEnter}
                      onMouseLeave={onMouseLeave}
                    >
                      <Button
                        variant='outlined'
                        color={!hasHover ? 'primary' : 'destructive'}
                        size='s'
                        onClick={(e) => {
                          removeQuestionSet(e, questionSet);
                          trackEvent({content: `remove ${questionSet.getName()}`});
                        }}
                        style={{minWidth: '64px'}}
                      >
                        {hasHover ? 'Remove' : 'Added'}
                      </Button>
                    </div>
                  )}
                </WithHoverAndFocus>
              ) : (
                <Button
                  size='s'
                  onClick={(e) => addQuestionSet(e, questionSet)}
                  style={{minWidth: '64px'}}
                >
                  Add
                </Button>
              )}
              <Icon icon='chevron-right' className='u-mar-l_1' />
            </div>
          </div>
        </ListGroupItem>
      </CellMeasurer>
    );
  };

  return (
    <div className='u-display_flex u-justify-content_center cd-question-list__column-add'>
      <ListGroup
        className='u-display_flex u-flex-direction_column cd-question-list--add'
        roundness='none'
      >
        {/* Todo: Make here to L388 own component? */}
        <ListGroupItem header className='u-display_flex u-align-items_center'>
          <Text bold className='u-mar-r_2 u-white-space_nowrap' size='m'>
            {totalQuestions} {pluralize('result', totalQuestions)}
          </Text>
          <div className='u-display_flex u-align-items_center'>
            <AddQuestionsDropdown maxQuestions={totalQuestions || 0} />
          </div>
          <Button
            className='u-display_flex u-align-items_center u-mar-l_auto'
            color='secondary'
            variant='text'
            onClick={() => {
              setSearchSort(searchSort === 'difficulty' ? '-difficulty' : 'difficulty');
              trackEvent({content: 'sort by difficulty'});
            }}
          >
            <Text bold size='m' className='u-white-space_nowrap'>
              Sort: Difficulty
            </Text>
            <Icon
              className='u-mar-l_1'
              icon={searchSort === 'difficulty' ? 'arrow-down' : 'arrow-up'}
            />
          </Button>
        </ListGroupItem>
        <div className='u-flex-grow_1 u-overflow_hidden'>
          <AutoSizer>
            {({width, height}) => (
              <VirtualizedList
                ref={listRef}
                height={height}
                width={width}
                rowHeight={cellMeasurerCache.current?.rowHeight || 100}
                onRowsRendered={handleRowsRendered}
                rowCount={questionSets.size}
                rowRenderer={rowRenderer}
                onScroll={updateScrollPosition}
                tabIndex={-1}
              />
            )}
          </AutoSizer>
          {isLoadingQuestionsList ? (
            <LoadingSpinner className='vss-student-dropdown__loader' size={2} />
          ) : null}
        </div>
        <ListGroupItem footer paddingLevel={0} className='questions-list-v2__footer'>
          <QuestionsListFooter
            activeQuestion={activeQuestion}
            setActiveQuestionState={setActiveQuestionState}
            scrollToQuestionById={scrollToQuestionByIdFunc}
            paginatedQuestionsListManager={paginatedResultsManager}
          />
        </ListGroupItem>
      </ListGroup>
    </div>
  );
};

export default ContentDiscoveryAddQuestionsList;
